import { Inject, Injectable } from '@angular/core';
import {
    ConfigConstants,
    ConfigService,
    NOTIFICATION_STATUS_CODE,
    NotificationModel,
    NotificationService,
    NotificationTimerModel,
    WindowService,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { BsModalService } from 'ngx-bootstrap/modal';
import { EtaAlertComponent } from '../../../shared/components/eta-alert/eta-alert.component';

@Injectable({
    providedIn: 'root',
})
export class AlertService {
    private readonly window: Window;
    private alertTimers: NotificationTimerModel[] = [];
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly notificationService: NotificationService,
        private readonly modalService: BsModalService,
        private readonly configService: ConfigService,
        private readonly windowService: WindowService,
    ) {
        this.window = this.windowService.nativeWindow;
    }

    /**
     * This function adds the timer based on the duration(in minutes) i.e time left to start the ETA.
     * The timer limit is derived from Config.
     * Once the duration left is less than or equal to the limit, An alert will be displayed for the Planned ETA.
     *
     * @param notification the notification for which the timer is set
     */
    public setTimerForEtaAlert(notification: NotificationModel): void {
        const durationLeft = moment
            .duration(moment(this.notificationService.getStatus(notification).startTime).diff(moment()))
            .asMinutes();
        const etaAlertTimerLimit = this.configService.getEtaAlertTimerLimit();
        if (durationLeft < 0) {
            return;
        }
        // calculating the time difference
        const timer = durationLeft > etaAlertTimerLimit ? durationLeft - etaAlertTimerLimit : 0;

        const notificationTimer = new NotificationTimerModel(
            notification.identifier,
            this.window.setTimeout(
                () => this.showEtaPopupModal(notification),
                timer * ConfigConstants.timeMultiplier * ConfigConstants.milliSecToSecConversionFactor,
            ),
        );

        this.alertTimers.push(notificationTimer);
    }

    /**
     * This function removes the already existing timer
     *
     * @param notification the notification for which the timer is removed
     */
    public removeEtaTimer(notification: NotificationModel): void {
        const notificationTimer = this.alertTimers.find((notificationTimerModel) =>
            notificationTimerModel.notificationId.equalsTo(notification.identifier),
        );
        if (notificationTimer) {
            this.window.clearTimeout(notificationTimer.timer);
        }
    }

    /**
     * This function gets all the notifications in ETA status.
     * If a notification is not escalated or if timer is not set,
     * then the function will set a timer for that notification to raise the ETA alert
     */
    public checkEtaAlertRequired(): void {
        const notifications = this.notificationService.getAllNotificationsInEtaStatus();

        notifications.forEach((notification) => {
            const isEscalated = this.notificationService.getEscalated(notification);
            if (isEscalated) {
                return;
            }

            const timerExists = this.checkEtaTimerAddedOnNotification(notification);
            if (timerExists) {
                return;
            }
            this.setTimerForEtaAlert(notification);
        });
    }

    /**
     * This function checks if timer is already added for this notification.
     * If yes,returns boolean true otherwise returns false
     *
     * @param notification the notification for which the timer is checked
     * @returns true if timer is already added, false otherwise
     */
    private checkEtaTimerAddedOnNotification(notification: NotificationModel): boolean {
        return this.alertTimers.some((timer) => timer.notificationId.equalsTo(notification.identifier));
    }

    /**
     * This function will open notification ETA Alert modal
     *
     * @param notification the notification for which the ETA is shown
     */
    private showEtaPopupModal(notification: NotificationModel): void {
        const status = this.notificationService.getStatus(notification);
        // checks if notification status is ETA
        const etaStatus = status.statusCode === NOTIFICATION_STATUS_CODE.ETA ? status : undefined;

        if (!etaStatus) {
            this.log.error(
                `AlertService : showEtaPopupModal : Tried to alert a notification without ETA status. ${notification.identifier.toString()}!`,
            );
            return;
        }
        this.modalService.show(EtaAlertComponent, {
            backdrop: 'static',
            class: 'modal-md',
            initialState: {
                notification: notification,
                etaStartTime: etaStatus.startTime,
            },
            ariaLabelledBy: 'eta-alert-modal-title',
        });
    }
}
